import { ofType, combineEpics } from 'redux-observable';
import { merge, of, timer } from 'rxjs';
import {
   delay,
   filter,
   ignoreElements,
   map,
   scan,
   switchMap,
   take,
   takeUntil,
   tap,
} from 'rxjs/operators';
import { getter } from 'property-expr';
import { ajax } from 'rxjs/ajax';

import {
   CLEAR_TUTORIAL,
   INIT_TUTORIAL,
   setActiveInterface,
   SET_TUTORIAL,
} from '../app/actions';
import {
   ADVANCE_TUTORIAL,
   init,
   triggerActions,
   startSkipTimer,
   START_SKIP_TIMER,
   setAllowSkip,
   INIT,
   TRIGGER_ACTIONS,
   awaitMatchedActions,
   AWAIT_MATCHED_ACTIONS,
   advanceTutorial,
   COMPLETE_TUTORIAL_STEP,
   completeTutorialStep,
   SUBMIT_QUIZ_ANSWER,
   setQuizAnswerCorrect,
   awaitMatchedState,
   AWAIT_MATCHED_STATE,
   CANCEL_USER_ACTION,
} from './actions';
import { objectIncludes } from '../util/objectIncludes';

import { comparisonTypes } from './constants';
import { simpleDeepEqual } from '../util/simpleDeepEqual';
import { assetsUrl } from '../util/constants';
import { loadLoop, setSuggestedValues } from '../interfaces/sequencer/actions';
import { setHighlightedControls } from '../shared/actions';
import { controllerValuesEqual } from '../util/controllerValuesEqual';

const getCurrentStep = (state$) =>
   state$.value.tutorial.tutorialDocument?.sections?.[
      state$.value.tutorial.section
   ]?.steps?.[state$.value.tutorial.step];

const removeFalsey = (obj) =>
   Object.entries(obj).reduce((newObj, [key, val]) => {
      if (val) {
         return { ...newObj, [key]: val };
      }
      return newObj;
   }, {});
const compareTruthy = (a, b) =>
   simpleDeepEqual(removeFalsey(a), removeFalsey(b));

const getQuizAnswerCorrect = (state$) => {
   const currentStep = getCurrentStep(state$);
   const stateProperty = getter(
      currentStep.assertion.propertyExpression,
      true
   )(state$.value);
   switch (currentStep.assertion.comparison) {
      case comparisonTypes.containsExactTruthyValues:
         return compareTruthy(currentStep.assertion.value, stateProperty);
      case comparisonTypes.equals:
      default:
         return simpleDeepEqual(currentStep.assertion.value, stateProperty);
   }
};

const ofActionsThatEndCurrentStep = ofType(ADVANCE_TUTORIAL);

const initEpic = (action$, state$) =>
   action$.pipe(
      ofType(INIT_TUTORIAL),
      // load tutorial
      switchMap(
         ({
            inlineTutorial,
            tutorialId,
            exerciseId,
            moduleId,
            secure,
            courseId,
         }) => {
            if (inlineTutorial) {
               return of(inlineTutorial);
            }

            let url;

            if (exerciseId) {
               url = `${assetsUrl}/${
                  secure ? 'secure' : 'public'
               }/exercise/${exerciseId}${
                  moduleId ? `/module/${moduleId}` : ''
               }/tutorial/${tutorialId}.json`;
            }

            if (courseId) {
               url = `${assetsUrl}/${
                  secure ? 'secure' : 'public'
               }/course/${courseId}/tutorial/${tutorialId}.json`;
            }

            return ajax({
               url,

               headers: secure
                  ? {
                       Authorization: `Bearer ${state$.value.appData.permissions.token}`,
                    }
                  : undefined,
               // withCredentials: true,
               // crossDomain: true,
            }).pipe(map(({ response }) => response));
         }
      ),
      switchMap((tutorialDocument) => {
         const loadActions = [];
         if (
            tutorialDocument.interface != null &&
            state$.value.activeInterface?.type !== tutorialDocument.interface
         ) {
            loadActions.push(
               of(setActiveInterface(tutorialDocument.interface))
            );
         }
         // load soundkit/loop
         if (
            tutorialDocument.preload?.loop != null &&
            state$.value.activeInterface?.loops?.cache?.[
               `${tutorialDocument.preload.loop.loopKitId}/${tutorialDocument.preload.loop.categoryId}/${tutorialDocument.preload.loop.loopId}`
            ] == null
         ) {
            const { loopKitId, categoryId, loopId } =
               tutorialDocument.preload.loop;
            loadActions.push(
               of(
                  loadLoop({
                     loopKitId,
                     categoryId,
                     loopId,
                  })
               )
            );
         }

         return merge(
            ...loadActions,
            timer(0, 100).pipe(
               filter(() => {
                  if (
                     tutorialDocument.preload?.loop != null ||
                     tutorialDocument.preload?.queuedLoop != null ||
                     tutorialDocument.preload?.currentLoop != null
                  ) {
                     let loop;

                     if (tutorialDocument.preload.loop != null) {
                        loop =
                           state$.value.activeInterface.loops.cache[
                              `${tutorialDocument.preload.loop.loopKitId}/${tutorialDocument.preload.loop.categoryId}/${tutorialDocument.preload.loop.loopId}`
                           ];
                     } else if (tutorialDocument.preload?.queuedLoop != null) {
                        const queued =
                           state$.value.activeInterface.loops.queue[0];
                        loop =
                           state$.value.activeInterface.loops.cache[
                              `${queued.loopKitId}/${queued.categoryId}/${queued.loopId}`
                           ];
                     } else if (tutorialDocument.preload?.currentLoop != null) {
                        loop = state$.value.activeInterface.loops.currentLoop;
                     }

                     if (!loop || loop.loading) {
                        return false;
                     }
                  }

                  // ..... alternatively use preload functionality ??????
                  // Make sure sound kit is loaded if sound kit based exercise
                  else if (state$.value.activeInterface.soundKit?.id) {
                     if (
                        state$.value.activeInterface.soundKits[
                           state$.value.activeInterface.soundKit.id
                        ] == null ||
                        state$.value.activeInterface.soundKits[
                           state$.value.activeInterface.soundKit.id
                        ].loading
                     ) {
                        return false;
                     }
                  }

                  return true;
               }),
               take(1),
               switchMap(() => of(init(tutorialDocument)))
            )
         );
      })
   );

const completeTutorialStepEpic = (action$, state$) =>
   action$.pipe(
      ofType(COMPLETE_TUTORIAL_STEP),
      switchMap(() => {
         const currentStep = getCurrentStep(state$);
         return merge(
            of(triggerActions(currentStep.postActions)),
            of(advanceTutorial())
         );
      })
   );

const submitQuizAnswerEpic = (action$, state$) =>
   action$.pipe(
      ofType(SUBMIT_QUIZ_ANSWER),
      switchMap(() => {
         const currentStep = getCurrentStep(state$);
         return merge(
            of(triggerActions(currentStep.quizSubmitActions)),
            of(setQuizAnswerCorrect(getQuizAnswerCorrect(state$)))
         );
      })
   );

const advanceTutorialEpic = (action$, state$) =>
   action$.pipe(
      ofType(ADVANCE_TUTORIAL, INIT),
      switchMap(() => {
         const currentStep = getCurrentStep(state$);
         return merge(
            of(setSuggestedValues(currentStep.suggestedValues || {})),
            of(triggerActions(currentStep.preActions)),
            of(
               startSkipTimer(
                  !!currentStep.requiredActions ||
                     !!currentStep.requiredInterfaceState
               )
            ),
            of(awaitMatchedActions(currentStep.requiredActions)),
            of(awaitMatchedState(currentStep.requiredInterfaceState)),
            of(setHighlightedControls(currentStep.highlightedControls || {}))
         );
      })
   );

const skipTimerEpic = (action$) =>
   action$.pipe(
      ofType(START_SKIP_TIMER),
      filter((action) => action.canSkip),
      switchMap(() =>
         of(setAllowSkip(true)).pipe(
            delay(10000),
            takeUntil(action$.pipe(ofActionsThatEndCurrentStep))
         )
      )
   );

const triggerActionsEpic = (action$) =>
   action$.pipe(
      ofType(TRIGGER_ACTIONS),
      filter((action) => !!action.actions),
      switchMap((action) =>
         merge(...action.actions.map((tutorialAction) => of(tutorialAction)))
      )
   );

// Note: objectIncludes is not a deepEqual. It only checks whether the first object deeply contains everything that the second object does
// so the first object may contain more entries than the second. If this becomes a problem, can have a prop in the tutorial step
// to determine whether to deepEqual or objectIncludes
const removeMatchedAction = (remainingActions, currentAction) =>
   remainingActions.filter(
      (action) =>
         !(
            action.type === currentAction.type &&
            objectIncludes(currentAction, action)
         )
   );
const awaitMatchedActionsEpic = (action$) =>
   action$.pipe(
      ofType(AWAIT_MATCHED_ACTIONS),
      filter((action) => !!action.actions),
      switchMap((action) =>
         action$.pipe(
            scan(removeMatchedAction, action.actions),
            filter((remainingActions) => remainingActions.length === 0),
            take(1),
            map(() => completeTutorialStep()),
            takeUntil(action$.pipe(ofActionsThatEndCurrentStep))
         )
      )
   );

const testStateCondition = (interfaceState, test) => {
   let matched;
   switch (test.condition) {
      case 'equal': {
         matched = getter(test.property, true)(interfaceState) === test.value;
         break;
      }
      case 'floatEqual': {
         matched = controllerValuesEqual(
            getter(test.property, true)(interfaceState),
            test.value
         );
         break;
      }
      case 'looseEqual': {
         const actualValue = getter(test.property, true)(interfaceState);
         matched =
            actualValue >= test.value - 0.015 &&
            actualValue <= test.value + 0.015;
         break;
      }
      case 'lessThan': {
         matched = getter(test.property, true)(interfaceState) < test.value;

         break;
      }
      case 'greaterThan': {
         matched = getter(test.property, true)(interfaceState) > test.value;

         break;
      }
      case 'isNull': {
         matched = getter(test.property, true)(interfaceState) == null;
         break;
      }
      default: {
         matched = false;
      }
   }

   if (test.not) {
      return !matched;
   }

   return matched;
};

const testGroup = (interfaceState, group) => {
   const criteriaMatchCount = group.conditions.reduce(
      (matchCount, currentTest) =>
         matchCount + (testStateCondition(interfaceState, currentTest) ? 1 : 0),
      0
   );

   if (group.oneOf) {
      const matchedOne = criteriaMatchCount === 1;
      return group.not ? !matchedOne : matchedOne;
   }

   const criteriaMatched = criteriaMatchCount === group.conditions.length;

   if (group.not) {
      return !criteriaMatched;
   }
   return criteriaMatched;
};

const awaitMatchedStateEpic = (action$, state$) =>
   action$.pipe(
      ofType(AWAIT_MATCHED_STATE),
      filter((action) => !!action.requiredState),
      switchMap((action) =>
         timer(0, 100).pipe(
            filter(() =>
               action.requiredState.reduce((matches, currentTest) => {
                  if (!matches) {
                     return false;
                  }

                  if (currentTest.group) {
                     return testGroup(
                        state$.value.activeInterface,
                        currentTest
                     );
                  }

                  return testStateCondition(
                     state$.value.activeInterface,
                     currentTest
                  );
               }, true)
            ),
            take(1),
            map(() => completeTutorialStep()),
            takeUntil(action$.pipe(ofActionsThatEndCurrentStep))
         )
      )
   );

const cancelUserActionEpic = (action$) =>
   action$.pipe(
      ofType(CANCEL_USER_ACTION),
      tap(() => {
         document.dispatchEvent(new CustomEvent('tutorial-cancel-user-action'));
      }),
      ignoreElements()
   );

const tutorialEpics = (action$, ...rest) =>
   combineEpics(
      initEpic,
      completeTutorialStepEpic,
      advanceTutorialEpic,
      skipTimerEpic,
      triggerActionsEpic,
      awaitMatchedActionsEpic,
      submitQuizAnswerEpic,
      awaitMatchedStateEpic,
      cancelUserActionEpic
   )(action$, ...rest).pipe(
      takeUntil(action$.pipe(ofType(SET_TUTORIAL, CLEAR_TUTORIAL)))
   );

export { tutorialEpics };
